import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnonymizerIntegrationReadiness } from '@/entities/anonymizer-config';
import { useFormGuardActions } from '@/entities/formGuard';
import { useHasPermission } from '@/entities/profile';
import { AnonymizerClassList } from './ui/AnonymizerClassList';
import { AnonymizerConfigCard } from './ui/AnonymizerConfigCard';
import { AnonymizerIntegrationGate } from './ui/AnonymizerIntegrationGate';
import { MasterDetailLayout, PageContainer } from '@/shared/ui';

export const AnonymizerConfigPage: FC = () => {
  const { t } = useTranslation('anonymizerConfig');
  const { '*': classPath } = useParams();
  const navigate = useNavigate();
  const { setIsDirty } = useFormGuardActions();

  // Гейтинг страницы по готовности интеграции анонимизатора: признак открыт всем, кому открыта
  // сама страница, — сведения об интеграции требуют права на раздел интеграций
  const {
    data: readiness,
    isLoading: isReadinessLoading,
    isLoadingError: isReadinessLoadError,
    isFetching: isReadinessFetching,
    refetch: refetchReadiness,
  } = useAnonymizerIntegrationReadiness();
  // Тем же правом закрыт маршрут списка интеграций — без него ссылка вела бы на «Страница не найдена»
  const canOpenIntegrations = useHasPermission('VIEW_INTEGRATION_INFO');

  const handleBack = () => navigate('/anonymizer-config');
  const handleOpenIntegrations = () => navigate('/integrations/list');
  const handleRetryReadiness = () => refetchReadiness();

  if (!readiness?.ready) {
    return (
      <PageContainer enableScroll={false} outerClassName="h-full" className="flex flex-col h-full gap-4 w-full">
        <h1 className="text-2xl font-semibold">{t('title')}</h1>
        <AnonymizerIntegrationGate
          isLoading={isReadinessLoading}
          isLoadError={isReadinessLoadError}
          isRetrying={isReadinessFetching}
          canOpenIntegrations={canOpenIntegrations}
          onRetry={handleRetryReadiness}
          onOpenIntegrations={handleOpenIntegrations}
        />
      </PageContainer>
    );
  }

  return (
    <MasterDetailLayout
      isDetailOpen={Boolean(classPath)}
      onBack={handleBack}
      toolbar={<h1 className="text-2xl font-semibold">{t('title')}</h1>}
      listClassName="@2xl:w-64"
      detailClassName="max-w-4xl"
      list={<AnonymizerClassList activeClass={classPath} />}
      detail={
        <AnonymizerConfigCard className={classPath} key={classPath} onUnsavedChange={(dirty) => setIsDirty(dirty)} />
      }
    />
  );
};
